import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';

import { formatSyncedDate, formatSyncedMoney } from '../../Assets/utils';
import AccountListPageHeader from '../AccountListPageHeader';
import { useAccountRelatedFullList } from '../useAccountRelatedRows';

import { toAccountExpense } from './toAccountExpense';
import type { AccountExpense } from './types';

const sharedOpts: Partial<GridColDef<AccountExpense>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const columns: Array<GridColDef<AccountExpense>> = [
  { field: 'name', headerName: 'Expense Name', flex: 2, minWidth: 220, ...sharedOpts },
  { field: 'type', headerName: 'Type', flex: 1, minWidth: 140, ...sharedOpts },
  {
    field: 'travelForm',
    headerName: 'Travel Form',
    flex: 2,
    minWidth: 220,
    renderCell: ({ row }) => row.travelForm || '—',
    ...sharedOpts,
  },
  {
    field: 'totalCost',
    headerName: 'Total Cost',
    flex: 1,
    minWidth: 140,
    align: 'right',
    headerAlign: 'right',
    // Each row is in its own currency, so sorting the amounts would mix them.
    sortable: false,
    renderCell: ({ row }) => formatSyncedMoney(row.currency, row.totalCost) ?? '—',
    ...sharedOpts,
  },
  {
    field: 'createdDate',
    headerName: 'Created Date',
    flex: 1,
    minWidth: 180,
    renderCell: ({ row }) => formatSyncedDate(row.createdDate) ?? '—',
    ...sharedOpts,
  },
];

export default function AccountExpensesListPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const { data: account } = useGetAccount(accountId);
  const {
    count,
    error,
    isLoading,
    isTruncated,
    rows: expenses,
  } = useAccountRelatedFullList(accountId, 'expenses', toAccountExpense);

  return (
    <>
      <Helmet>
        <title>Expenses | {account?.name || accountId}</title>
      </Helmet>

      <AccountListPageHeader
        accountId={accountId}
        count={count}
        isTruncated={isTruncated}
        sortedBy="Total Cost (AUD)"
        title="Expenses"
      />

      {error ? (
        <Alert severity="error">Could not load expenses: {error.message}</Alert>
      ) : (
        <DataGrid
          autoHeight
          disableRowSelectionOnClick
          columns={columns}
          loading={isLoading}
          rows={expenses}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      )}
    </>
  );
}
